import Icon from '@components/icon';
import Select, { SelectOption } from '@components/select';
import pluralize from 'ati-utils/pluralize';
import cn from 'clsx';
import { observer } from 'mobx-react-lite';

import { Field } from 'components/Field';
import { SearchResultsPagination } from 'components/SearchResultsPagination';
import { itemsPerPageCookieName } from 'constants/ui';
import { useStore } from 'hooks/useStore';
import { PrettyLoad } from 'layout/Search/PrettyLoad';
import { PopupIds } from 'store/UIStore/Popups';
import { tabIds } from 'store/UIStore/UIStore';
import { camelToSnakeObj, cleanEmpty } from 'utils/objects';
import { tweakDescriptorForTendersCard } from 'utils/prettyLoad';
import { cookie } from 'utils/storage';
import { interpolate } from 'utils/string/format';

import { Sorting } from '../Filters/Sorting';

import css from './SearchResults.module.css';

const SearchResults = () => {
  const {
    app,
    ui,
    view,
    profile,
    savedFilters,
    filter: { savedFilter },
    favorites,
  } = useStore();

  if (!app.searchResult) return null;

  const handleItemsPerPageChange = (option: SelectOption) => {
    if (!profile.data?.isUser) {
      return ui.popups.open(PopupIds.loginPopup);
    }

    if (!profile.data?.isPaidUser) {
      return ui.popups.open(PopupIds.onlyPaidPopup);
    }

    ui.setPage(1);
    ui.itemsPerPage.setOption(option);
    cookie.set({ name: itemsPerPageCookieName, value: option.value, days: 365 });

    app.searchLoads({ isNewSearch: false });
  };

  const handleSubscription = () => {
    savedFilters.openFilterPopup(savedFilter?.savedFilter, { enableSubscription: true });
  };

  const shouldShowSubscribeButton = savedFilters.canSaveFilter;
  const descriptor = tweakDescriptorForTendersCard(camelToSnakeObj(cleanEmpty(app.getRequestData(false))));

  if (ui.activeTab === tabIds.favorites) return null;

  return (
    <div>
      <div className={css.searchStatus}>
        <div className={css.summary}>
          <h2 className={cn(css.totalItems, 'glz-h glz-is-size-2')}>
            {pluralize({ value: app.searchResult?.totalItems, ...app.i18n.app.declensions.found })}{' '}
            {view.pluralizedCount(app.searchResult?.totalItems, 'loads')}
          </h2>

          {Boolean(app.hiddenLoadsCount) && (
            <span className={css.hiddenLoads}>
              {isFinite(app.hiddenLoadsCount) && (
                <span>
                  {interpolate(app.i18n.app.hiddenLoadsNumber, { hiddenLoadsCount: app.hiddenLoadsCount.toString() })}
                </span>
              )}
              {!isFinite(app.hiddenLoadsCount) && <span>{app.i18n.app.hasHiddenLoads}</span>}

              <a
                className={cn(css.showLoads, 'glz-link glz-is-medium glz-is-gray glz-is-underline')}
                onClick={app.showHiddenLoads}
              >
                {app.i18n.app.showLoads}
              </a>
            </span>
          )}
        </div>

        <Sorting />

        {ui.totalPages > 1 && <SearchResultsPagination className={css.pagination} />}
      </div>

      <div className={cn(css.actionsContainer, css.actionsContainer_top)}>
        {shouldShowSubscribeButton && (
          <div className={css.subscriptionContainer}>
            {savedFilter?.hasSubscription ? (
              <>
                <button
                  className={cn(css.subscriptionButton, 'glz-button glz-is-small glz-is-primary')}
                  onClick={handleSubscription}
                >
                  {app.i18n.alreadySubscribed.button}
                </button>
                <span className={css.subscriptionText}>{app.i18n.alreadySubscribed.desc}</span>
              </>
            ) : (
              <>
                <button className={cn(css.subscriptionButton, 'glz-button glz-is-small')} onClick={handleSubscription}>
                  {app.i18n.subscribe.button}
                </button>
                <span className={css.subscriptionText}>{app.i18n.subscribe.desc}</span>
              </>
            )}
          </div>
        )}

        <Field
          isRow
          containerClassName={css.sortFieldContainer}
          contentClassName={css.sortFieldContent}
          labelClassName={css.sortLabel}
          labelText={app.i18n.app.showLines}
        >
          <Select
            className={css.sortSelect}
            isSearchable={false}
            options={ui.itemsPerPage.options}
            value={ui.itemsPerPage.option}
            onChange={handleItemsPerPageChange}
          />
        </Field>
      </div>

      {!app.hasLoads && (
        <div className={css.noResultsContainer}>
          <Icon className={css.noResultsIcon} color="var(--dark-gray)" height={40} name={'ic_cargo'} width={40} />
          <span className="glz-h glz-is-size-3">{app.i18n.app.noResults}</span>
        </div>
      )}

      <PrettyLoad
        {...app.searchResult}
        callbacks={{
          hideLoads: app.onLoadsHide,
          selectLoads: app.selectedLoads.toggleSelection,
          onRemoveLoadFromFavorites: favorites.fetchFavorites,
          onAddLoadToFavorites: favorites.fetchFavorites,
          onFavoritesFAQPopoverClose: favorites.handleFavoritesFAQPopoverClose,
        }}
        currentPage={ui.page}
        descriptor={descriptor}
      />

      {ui.totalPages > 1 && (
        <div className={cn(css.actionsContainer, css.actionsContainer_bottom)}>
          <SearchResultsPagination />

          <Field
            isRow
            containerClassName={css.sortFieldContainer}
            contentClassName={css.sortFieldContent}
            labelClassName={css.sortLabel}
            labelText={app.i18n.app.showLines}
          >
            <Select
              className={css.sortSelect}
              isSearchable={false}
              options={ui.itemsPerPage.options}
              value={ui.itemsPerPage.option}
              onChange={handleItemsPerPageChange}
            />
          </Field>
        </div>
      )}
    </div>
  );
};

const connected = observer(SearchResults);

export { connected as SearchResults };
